/**
 * ⭐⭐ `AW30` · **El botón «nueva conversación», que NO crea ninguna.**
 *
 * Pedido del dueño, 28/8/2026, requerimiento 2: *«si quiero crear una nueva se
 * debería cerrar automáticamente la activa»*. Y «conversación» acá es **la ventana
 * de 24 h —la sesión—**, no el hilo largo: `id_conversacion` no vence y no se
 * cierra (`contracts/conversation`, `apps/admin-web/src/cierre.ts`).
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ① EL BOTÓN NO PROMETE LO QUE NO CUMPLE — `ADR-0040` §②
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **Cerrar no abre nada.** El borde del servidor lo dice textual: *«No abre una
 * sesión nueva. Cerrar es cerrar; la próxima la abre el próximo `registrar`»*. ⇒
 * un botón rotulado «Nueva conversación» que sólo cierra **miente en el rótulo**:
 * el operador lo aprieta, no aparece ninguna sesión nueva, y lee un defecto donde
 * hay un sistema andando. Por eso el rótulo dice lo que el gesto hace —**cerrar
 * la sesión vigente**— y el texto de al lado dice quién abre la próxima y cuándo.
 *
 * ⚠️ Y **cerrar tampoco silencia ni rechaza**: el participante puede escribir en
 * el instante siguiente y arranca una ventana nueva (`ventana.ts:117`, y
 * `cierre.ts` ⟦el-cierre-no-inventa-una-regla⟧). Eso se dice **en la
 * confirmación**, que es donde alguien se está preguntando qué va a romper.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ② `null` NO SE INTERPRETA. ES EL REQUISITO MÁS FÁCIL DE ROMPER DE ACÁ
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El borde contesta `200` con `{"id_sesion": null}` **tanto cuando el hilo no
 * tenía ninguna sesión abierta como cuando ese hilo no existe** — `cerrarSesion`
 * no tiene variante de ausencia, así que del otro lado **no hay un `404` que se
 * pueda escribir sin mentir** (`cierre.ts` ⟦el-hilo-viene-en-la-ruta⟧).
 *
 * ⇒ esta pantalla **no puede distinguirlas, y por lo tanto no las nombra.** Dice
 * lo único afirmable —**no se cerró ninguna sesión**— y declara que las dos causas
 * posibles contestan igual. La salida cómoda —«no había ninguna sesión abierta»—
 * es una afirmación que el cable no respalda, y es exactamente la que un operador
 * usaría para descartar que el identificador esté mal.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ③ SE CONFIRMA ANTES, CON LA FIGURA QUE EL REPO YA TIENE
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Es una escritura destructiva sobre el estado del ensayo, y **la última que este
 * producto agregó** —eliminar un documento del banco— ya fijó cómo se pide
 * permiso: `banco-de-contenido/dialogo.tsx`, con foco atrapado, Escape, y el foco
 * de vuelta al botón que abrió. **Se reusa ese componente**, no se estrena un
 * segundo modal: un `window.confirm()` o un `<dialog>` propio serían una segunda
 * accesibilidad de modal para mantener, y la de acá arrancaría peor.
 *
 * ⛔ **Y no hay optimismo:** nada cambia en la pantalla hasta que el borde
 * contestó, igual que `acciones.tsx` deja la fila en la tabla mientras el borde
 * trabaja. Lo que se dibuja después es **lo que el borde contestó**, no lo que se
 * pidió.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ④ NO BLOQUEA NADA POR VENTANA
 * ════════════════════════════════════════════════════════════════════════════
 *
 * El único `disabled` de este archivo es **«hay un pedido en vuelo»**, y existe
 * para no mandar dos `POST` con un doble clic. No depende del vencimiento ni de la
 * ventana: `gates/simulador-operable.spec.ts` (`J3`) pone rojo cualquier `disabled`
 * que sí lo haga, y tiene razón — el motor no rechaza al vencer, abre una sesión
 * nueva. Cerrar una sesión ya vencida es un gesto legítimo y sigue disponible.
 *
 * ⚠️ **Tampoco se esconde el botón cuando la ventana venció.** Esconderlo sería el
 * mismo bloqueo escrito de otra forma, y el borde es idempotente: la segunda
 * llamada contesta `null` y no rompe nada.
 */

import { useState } from "react"
import type { JSX } from "react"
import { Dialogo } from "../banco-de-contenido/dialogo.tsx"

/**
 * Lo que el borde contestó, sin traducir a un `boolean`.
 *
 * ⛔ `sin-cierre` **no dice por qué**: ver ② del encabezado. Es el nombre del
 * desenlace observable —no se cerró nada—, no el de una causa.
 */
type Resultado =
  | { readonly caso: "cerro"; readonly id_sesion: string }
  | { readonly caso: "sin-cierre" }
  | { readonly caso: "fallo"; readonly mensaje: string }

export function CierreDeSesion({
  id_conversacion,
  onCerrar,
}: {
  /**
   * ⭐ **El hilo largo, que es lo que el puerto recibe.** Sale de
   * `FilaDeMensaje.id_conversacion`, que ya viaja en cada fila de
   * `GET conversaciones` — el mismo cable que `AW28` trajo.
   *
   * ⛔ **No se identifica por participante y no viaja ningún teléfono**: este
   * identificador es opaco y ya está del lado del cliente.
   */
  readonly id_conversacion: string
  /**
   * Cierra y **relee**. Devuelve el `id_sesion` que el borde contestó, o `null`.
   *
   * ⛔ La relectura no es de este componente: el historial vive en `chat.tsx` y
   * quien lo pide es quien lo tiene. Acá sólo se dibuja lo que volvió.
   */
  readonly onCerrar: (id_conversacion: string) => Promise<string | null>
}): JSX.Element {
  const [confirmando, setConfirmando] = useState(false)
  const [enCurso, setEnCurso] = useState(false)
  const [resultado, setResultado] = useState<Resultado | null>(null)

  const cerrar = async (): Promise<void> => {
    if (enCurso) return
    setEnCurso(true)
    try {
      const id_sesion = await onCerrar(id_conversacion)
      setResultado(id_sesion === null ? { caso: "sin-cierre" } : { caso: "cerro", id_sesion })
      setConfirmando(false)
    } catch (causa) {
      setResultado({
        caso: "fallo",
        mensaje: causa instanceof Error ? causa.message : String(causa),
      })
    } finally {
      setEnCurso(false)
    }
  }

  return (
    <div className="sim__antecedentes-cierre">
      <button
        type="button"
        className="boton boton--peligro"
        /* ⛔ Sólo por «hay un pedido en vuelo». Ver ④ del encabezado. */
        disabled={enCurso}
        onClick={() => {
          setResultado(null)
          setConfirmando(true)
        }}
      >
        Cerrar la sesión vigente
      </button>

      {/*
        ⛔⛔ EL RÓTULO NO PROMETE UNA CONVERSACIÓN NUEVA Y ESTA LÍNEA TAMPOCO.
        La sesión nueva la abre el motor con el próximo mensaje, y decirlo acá
        —antes de apretar— es lo que evita que alguien lea «no funcionó» cuando
        el sistema hizo exactamente lo que dijo.
      */}
      <span className="sim__ayuda">
        Cerrar <strong>no crea</strong> una conversación nueva: la sesión siguiente la abre el
        motor con el próximo mensaje del participante. El hilo largo{" "}
        <code>{id_conversacion}</code> no cambia.
      </span>

      {resultado !== null && resultado.caso === "cerro" && (
        <p className="sim__ayuda" role="status">
          Se cerró la sesión <code>{resultado.id_sesion}</code>. Todavía no hay ninguna sesión
          nueva: la abre el motor cuando llegue el próximo mensaje.
        </p>
      )}

      {/*
        ⛔⛔ ACÁ SE DICE LO QUE PASÓ Y **NO** POR QUÉ. Ver ② del encabezado: el
        borde contesta lo mismo para «no había sesión abierta» y para «ese hilo no
        existe», y elegir una de las dos sería una afirmación de la pantalla sobre
        algo que el cable no dice.
      */}
      {resultado !== null && resultado.caso === "sin-cierre" && (
        <p className="sim__ayuda" role="status">
          <strong>No se cerró ninguna sesión.</strong> El borde contestó sin ningún{" "}
          <code>id_sesion</code>, y esa respuesta es la misma para dos situaciones distintas —que
          el hilo no tuviera ninguna sesión abierta, o que ese hilo no exista—: no hay con qué
          distinguirlas desde acá, así que no se afirma cuál fue.
        </p>
      )}

      {resultado !== null && resultado.caso === "fallo" && (
        <p className="simulador__error" role="alert">
          No se pudo cerrar la sesión: {resultado.mensaje} Este pedido va contra{" "}
          <code>admin-web</code> con tu sesión de operador, igual que la lectura de arriba y a
          diferencia del resto del simulador.
        </p>
      )}

      {confirmando && (
        <Dialogo titulo="Cerrar la sesión vigente" onCerrar={() => setConfirmando(false)}>
          <p>
            Se va a cerrar la ventana de 24 h abierta en el hilo{" "}
            <code>{id_conversacion}</code>.
          </p>
          {/*
            Las dos cosas que hay que saber ANTES de confirmar, y ninguna de las dos
            se puede deducir del rótulo del botón.

            ⛔ **Y NO van con `.sim__ayuda`**, que es el texto chico y atenuado de
            apoyo de un campo: acá son lo que hay que leer antes de decidir, y
            atenuarlas sería esconder la consecuencia debajo del botón que la causa.
          */}
          <p>
            <strong>No se abre ninguna sesión nueva.</strong> La próxima la abre el motor cuando
            llegue el próximo mensaje del participante, y recién ahí existe.
          </p>
          <p>
            <strong>Esto no silencia ni bloquea a nadie.</strong> El participante puede escribir
            en el instante siguiente: el motor no rechaza, abre una ventana nueva. Lo que se
            pierde es la continuidad de la sesión actual, y no hay forma de reabrirla.
          </p>

          {resultado !== null && resultado.caso === "fallo" && (
            <p className="simulador__error" role="alert">
              No se pudo cerrar la sesión: {resultado.mensaje}
            </p>
          )}

          <footer className="dialogo__acciones">
            <button
              type="button"
              className="boton boton--peligro-solido"
              disabled={enCurso}
              onClick={() => {
                void cerrar()
              }}
            >
              {enCurso ? "Cerrando…" : "Sí, cerrar la sesión"}
            </button>
            <button
              type="button"
              className="boton"
              disabled={enCurso}
              onClick={() => setConfirmando(false)}
            >
              Cancelar
            </button>
          </footer>
        </Dialogo>
      )}
    </div>
  )
}
